import { CUT } from "@/config/designer";
import type { SampleRegion } from "@/core/field/brushes";
import type { VoxelField } from "@/core/field/VoxelField";
import { chooseSurvivingComponent, labelSolidComponents } from "./components";
import { extendPolyline, PolylineDistance2D } from "./polylineDistance";

/**
 * A cut, described independently of the camera that produced it.
 *
 * `origin`, `right` and `up` define the plane the line was drawn on; the sweep
 * runs perpendicular to it, straight through the block. Keeping the engine
 * free of camera types means the cut can be unit-tested without a renderer,
 * and replayed exactly for redo.
 */
export interface CutSweep {
  readonly origin: readonly [number, number, number];
  readonly right: readonly [number, number, number];
  readonly up: readonly [number, number, number];
  /** The drawn line, in millimetres on the (right, up) plane. */
  readonly points: ReadonlyArray<readonly [number, number]>;
}

export interface CutResult {
  applied: boolean;
  reason?: string;
  /** Voxels removed as offcut, not counting the kerf itself. */
  discardedVoxels: number;
  keptVoxels: number;
  pieces: number;
  /** Samples the cut actually wrote to, so only those chunks are re-meshed. */
  region: SampleRegion | null;
}

/**
 * Saw straight through the foam along the drawn line and throw the offcut
 * away.
 *
 * Three steps, in the order a bandsaw does them:
 *   1. Remove a kerf-thick slab along the swept line. The line is extended
 *      well past both ends first, so the cut always goes right through.
 *   2. Work out what is still joined to what.
 *   3. Keep the largest piece that is still on the mounting plane; everything
 *      else has come off and is deleted.
 *
 * Like carving, this only ever raises field values, so the solid stays a
 * subset of the original blank and the flat rear face is untouched.
 */
export function applyCut(field: VoxelField, sweep: CutSweep): CutResult {
  const empty: CutResult = {
    applied: false,
    discardedVoxels: 0,
    keptVoxels: 0,
    pieces: 0,
    region: null,
  };
  if (sweep.points.length < 2) {
    return { ...empty, reason: "Draw a line across the hold first." };
  }

  const diagonal = Math.hypot(field.width, field.height, field.depth);
  const line = extendPolyline(sweep.points, diagonal * CUT.extendFactor);
  const kerf = Math.max(field.h * CUT.kerfVoxels, CUT.minKerfMm);

  const { data, dirty, nx, ny, nz } = field;
  const [ox, oy, oz] = sweep.origin;
  const [rx, ry, rz] = sweep.right;
  const [ux, uy, uz] = sweep.up;

  // The distance grid only has to span the block; the extended line reaches
  // far beyond it but never needs to be sampled out there.
  const bounds = blockPlaneBounds(field, sweep);
  const grid = new PolylineDistance2D(
    line,
    bounds.uMin - kerf * 2,
    bounds.vMin - kerf * 2,
    bounds.uMax + kerf * 2,
    bounds.vMax + kerf * 2,
    Math.max(field.h * CUT.distanceGridVoxels, 0.25),
  );

  let kerfTouched = 0;
  let i0 = nx;
  let i1 = 0;
  let j0 = ny;
  let j1 = 0;
  let k0 = nz;
  let k1 = 0;
  const mark = (i: number, j: number, k: number): void => {
    if (i < i0) i0 = i;
    if (i >= i1) i1 = i + 1;
    if (j < j0) j0 = j;
    if (j >= j1) j1 = j + 1;
    if (k < k0) k0 = k;
    if (k >= k1) k1 = k + 1;
  };

  for (let k = 0; k < nz; k++) {
    const z = field.sampleZ(k);
    for (let j = 0; j < ny; j++) {
      const y = field.sampleY(j);
      const row = field.index(0, j, k);
      for (let i = 0; i < nx; i++) {
        const p = row + i;
        const previous = data[p] as number;
        // Only material can be cut; air is already gone.
        if (previous >= kerf) continue;

        const x = field.sampleX(i);
        const dx = x - ox;
        const dy = y - oy;
        const dz = z - oz;
        const u = dx * rx + dy * ry + dz * rz;
        const v = dx * ux + dy * uy + dz * uz;

        const distance = grid.sample(u, v);
        if (!Number.isFinite(distance)) continue;
        const value = kerf - distance;
        if (value > previous) {
          data[p] = field.clampValue(value);
          dirty[p] = 1;
          mark(i, j, k);
          if (previous < 0) kerfTouched++;
        }
      }
    }
  }

  if (kerfTouched === 0) {
    return {
      ...empty,
      reason: "That line missed the hold.",
      region: i1 > i0 ? { i0, i1, j0, j1, k0, k1 } : null,
    };
  }

  const labelling = labelSolidComponents(field);
  const pieces = labelling.sizes.length;
  if (pieces === 0) {
    return { ...empty, reason: "That cut would remove everything.", pieces: 0 };
  }

  const survivor = chooseSurvivingComponent(labelling);
  const keptVoxels = labelling.sizes[survivor] as number;
  let discardedVoxels = 0;
  for (let c = 0; c < pieces; c++) {
    if (c !== survivor) discardedVoxels += labelling.sizes[c] as number;
  }

  if (discardedVoxels > 0) {
    const { labels } = labelling;
    for (let p = 0; p < labels.length; p++) {
      const label = labels[p] as number;
      if (label === -1 || label === survivor) continue;
      data[p] = field.clamp;
      dirty[p] = 1;
      const i = p % nx;
      const rest = (p - i) / nx;
      const j = rest % ny;
      mark(i, j, (rest - j) / ny);
    }
  }

  return {
    applied: true,
    discardedVoxels,
    keptVoxels,
    pieces,
    region: i1 > i0 ? { i0, i1, j0, j1, k0, k1 } : null,
  };
}

/** The block's extent on the plane the line was drawn on. */
function blockPlaneBounds(
  field: VoxelField,
  sweep: CutSweep,
): { uMin: number; uMax: number; vMin: number; vMax: number } {
  let uMin = Infinity;
  let uMax = -Infinity;
  let vMin = Infinity;
  let vMax = -Infinity;

  for (let corner = 0; corner < 8; corner++) {
    const x = corner & 1 ? field.width : 0;
    const y = corner & 2 ? field.height : 0;
    const z = corner & 4 ? field.depth : 0;
    const dx = x - sweep.origin[0];
    const dy = y - sweep.origin[1];
    const dz = z - sweep.origin[2];
    const u = dx * sweep.right[0] + dy * sweep.right[1] + dz * sweep.right[2];
    const v = dx * sweep.up[0] + dy * sweep.up[1] + dz * sweep.up[2];
    if (u < uMin) uMin = u;
    if (u > uMax) uMax = u;
    if (v < vMin) vMin = v;
    if (v > vMax) vMax = v;
  }
  return { uMin, uMax, vMin, vMax };
}
